import { JSX, useState } from 'react';
import { Button } from 'nhsuk-react-components';
import Alert from 'components/Alert';
import useSubmitConfig from 'queries/mutate/submitConfig';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign';
import useCampaignNavigate from 'hooks/useCampaignNavigate';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema';
import useUser from 'hooks/useUser.ts';
import { API_SUBMIT_CONFIG_ERROR } from 'constants/errorMessages';

export interface RequestSummaryReportButtonProps {
  teamId: string;
  iterationId: string;
  campaignId: string;
  iterationStatus?: string;
  hasUpdateRole: boolean;
}

export default function RequestSummaryReportButton({
  teamId,
  iterationId,
  campaignId,
  iterationStatus,
  hasUpdateRole,
}: RequestSummaryReportButtonProps) {
  const user = useUser();
  const [errorTitle, setErrorTitle] = useState<string>('Summary report not available');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | JSX.Element | null>(null);
  const [errorButtonText, setErrorButtonText] = useState<string>('Close');
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });
  const { navigateToCampaign } = useCampaignNavigate();

  const submitConfig = useSubmitConfig();

  const handleSubmitConfig = async () => {
    try {
      setIsSubmitting(true);
      await submitConfig.mutateAsync({
        teamId,
        iterationId,
        campaignId,
      });
      await fetchSpecificCampaign.refetch();
      navigateToCampaign(`iteration/${iterationId}/summary-report-requested`);
    } catch (e) {
      if (e instanceof Error && e.message === 'A selected cohort has been withdrawn') {
        setErrorTitle('Cohort(s) removed');
        setErrorMessage(
          <>
            <p>One or more of your cohorts is no longer available.</p>
            <p className="nhsuk-u-margin-bottom-0"><b>What you need to do next</b></p>
            <ol className="nhsuk-u-margin-bottom-0">
              <li className="nhsuk-u-margin-bottom-0">Delete any cohorts which are &apos;removed from team&apos;.</li>
              <li className="nhsuk-u-margin-bottom-0">Review all your cohorts and change if necessary.</li>
              <li className="nhsuk-u-margin-bottom-0">Review all your rules, as they may be affected, and change if necessary.</li>
            </ol>
          </>,
        );
        setErrorButtonText('I understand');
        return;
      }
      setErrorTitle('Summary report not available');
      setErrorMessage(API_SUBMIT_CONFIG_ERROR);
      setErrorButtonText('Close');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canShow = iterationStatus === ITERATION_STATUS.APPROVED
    && hasUpdateRole
    && user.roles.includes(`${teamId}:release`);

  if (!canShow) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleSubmitConfig}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Requesting...' : 'Request summary report'}
      </Button>

      {errorMessage && (
        <div className="nhsuk-u-margin-top-3 nhsuk-u-width-full">
          <Alert
            title={errorTitle}
            message={errorMessage}
            buttonText={errorButtonText}
            onClose={() => setErrorMessage(null)}
          />
        </div>
      )}
    </>
  );
}
